// Customer-configurable homepage sections (Homepage Builder → "Add section").
//   tile_showcase  — one tall feature tile + horizontally scrolling rows of tiles
//   category_cards — a row of cards, each with a title/arrow and a 2x2 grid of image+label links
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// device: "mobile" | "desktop" | anything else = both
export const deviceClass = (device, show = "block") =>
  device === "mobile" ? `${show} md:hidden` : device === "desktop" ? `hidden md:${show}` : "";

const isOn = (it) => !!it && it.enabled !== false;
const PASTELS = ["#fbd5c8", "#e7e3dc", "#d6e4e0", "#f3d0ee", "#cfe0f7", "#f8e6a0", "#e5d9f3", "#d9efc0", "#f9c9a8", "#f4efd3"];

function SmartLink({ to, className, style, children }) {
  const href = (to || "").trim();
  if (!href) return <div className={className} style={style}>{children}</div>;
  if (href.startsWith("/")) return <Link to={href} className={className} style={style}>{children}</Link>;
  return <a href={href} className={className} style={style} target="_blank" rel="noreferrer">{children}</a>;
}

function TagBadge({ text }) {
  if (!text) return null;
  return <span className="absolute top-2 left-2 z-10 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded shadow">{text}</span>;
}

function Header({ cfg }) {
  const showT = cfg.show_title !== false && !!cfg.title;
  const showS = cfg.show_subtitle !== false && !!cfg.subtitle;
  if (!showT && !showS) return null;
  return (
    <div className="mb-4">
      {showT && <h2 className="text-xl font-black text-slate-900">{cfg.title}</h2>}
      {showS && <p className="text-xs text-slate-500 mt-0.5">{cfg.subtitle}</p>}
    </div>
  );
}

function Tile({ item, index, w, h }) {
  return (
    <SmartLink
      to={item.link_url}
      className={`relative flex-shrink-0 rounded-2xl overflow-hidden group ${deviceClass(item.device)}`}
      style={{ width: w, height: h, backgroundColor: item.bg || PASTELS[index % PASTELS.length] }}
    >
      <TagBadge text={item.tag} />
      {item.title && <p className="absolute top-3 left-3 right-3 z-10 text-lg font-black leading-tight text-slate-900">{item.title}</p>}
      {item.image_url && (
        <img src={item.image_url} alt={item.title || ""} className="absolute inset-x-0 bottom-0 w-full h-[72%] object-contain object-bottom group-hover:scale-105 transition-transform duration-300" />
      )}
    </SmartLink>
  );
}

function ScrollRow({ items, rowIndex }) {
  const ref = useRef(null);
  const list = items.filter(isOn);
  if (list.length === 0) return null;
  const big = rowIndex === 0;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 480, behavior: "smooth" });
  return (
    <div className="relative">
      <div ref={ref} className="flex gap-3 overflow-x-auto scroll-smooth pb-1" style={{ scrollbarWidth: "none" }}>
        {list.map((it, i) => <Tile key={i} item={it} index={i + rowIndex * 3} w={big ? 200 : 150} h={big ? 280 : 180} />)}
      </div>
      {list.length > 4 && (
        <>
          <button type="button" onClick={() => scroll(-1)} aria-label="Scroll left" className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 w-9 h-14 bg-white shadow rounded-r-lg items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
          <button type="button" onClick={() => scroll(1)} aria-label="Scroll right" className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 w-9 h-14 bg-white shadow rounded-l-lg items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
        </>
      )}
    </div>
  );
}

export function TileShowcaseSection({ config = {} }) {
  const feature = config.feature && isOn(config.feature) && (config.feature.title || config.feature.image_url) ? config.feature : null;
  const rows = (config.rows || []).filter(r => (r.items || []).some(isOn));
  if (!feature && rows.length === 0) return null;
  return (
    <div className="bg-white py-6">
      <div className="max-w-[1800px] mx-auto px-4">
        <Header cfg={config} />
        <div className="flex flex-col md:flex-row gap-3">
          {feature && (
            <SmartLink
              to={feature.link_url}
              className={`relative rounded-2xl overflow-hidden md:w-[22%] md:min-w-[220px] h-[260px] md:h-auto md:min-h-[480px] flex-shrink-0 group ${deviceClass(feature.device)}`}
              style={{ backgroundColor: feature.bg || PASTELS[0] }}
            >
              <TagBadge text={feature.tag} />
              {feature.title && <p className="absolute top-4 left-4 right-4 z-10 text-3xl font-black leading-tight text-slate-900 whitespace-pre-line">{feature.title}</p>}
              {feature.image_url && <img src={feature.image_url} alt={feature.title || ""} className="absolute inset-x-0 bottom-0 w-full h-[75%] object-contain object-bottom group-hover:scale-105 transition-transform duration-300" />}
            </SmartLink>
          )}
          <div className="flex-1 min-w-0 space-y-3">
            {rows.map((r, i) => <ScrollRow key={i} items={r.items || []} rowIndex={i} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

// Resolve a card's four entries: manual items, or auto-filled from categories / products
function cardEntries(card, products, collections) {
  const mode = card.fill || "manual";
  if (mode === "categories") {
    return (collections || []).slice(0, 4).map(c => {
      const slug = c.slug || (c.name || "").toLowerCase().replace(/\s+/g, "-");
      const match = (products || []).find(p => (p.collections || []).some(x => x?.toLowerCase() === (c.name || "").toLowerCase()));
      return { label: c.name, image_url: c.image || match?.images?.[0] || match?.image || "", link_url: `/c/${slug}` };
    });
  }
  if (mode === "products") {
    const col = (card.collection || "").toLowerCase();
    return (products || [])
      .filter(p => !col || (p.category || "").toLowerCase() === col || (p.collections || []).some(x => x?.toLowerCase() === col))
      .slice(0, 4)
      .map(p => ({ label: p.name || p.item_name, image_url: p.images?.[0] || p.image || "", link_url: `/product/${p.id}` }));
  }
  return (card.items || []).filter(isOn).slice(0, 4);
}

export function CategoryCardsSection({ config = {}, products = [], collections = [] }) {
  const cards = (config.cards || []).filter(isOn);
  if (cards.length === 0) return null;
  return (
    <div className="bg-white py-6">
      <div className="max-w-[1800px] mx-auto px-4">
        <Header cfg={config} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map((card, ci) => {
            const entries = cardEntries(card, products, collections);
            if (entries.length === 0 && !card.title) return null;
            return (
              <div key={ci} className={`border border-slate-200 rounded-2xl p-4 bg-white ${deviceClass(card.device)}`}>
                <SmartLink to={card.link_url} className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-black text-slate-900 leading-tight">{card.title}</h3>
                  {card.link_url && <ChevronRight className="w-5 h-5 text-slate-700 flex-shrink-0" />}
                </SmartLink>
                <div className="grid grid-cols-2 gap-3">
                  {entries.map((e, i) => (
                    <SmartLink key={i} to={e.link_url} className={`block group ${deviceClass(e.device)}`}>
                      <div className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                        {e.image_url && <img src={e.image_url} alt={e.label || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />}
                      </div>
                      <p className="text-xs text-slate-700 mt-1.5 line-clamp-2">{e.label}</p>
                    </SmartLink>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
